import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { popperContentMotionClassNames, popperContentSideOffsetClassNames } from '@magicschool/spellbook/lib/popperContent';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useDirection } from 'radix-ui/direction';
import * as PopoverPrimitive from 'radix-ui/popover';
import { popoverContentClassName } from './sharedClasses';

export const PopoverTriggerSlot = Slot<React.ComponentProps<typeof PopoverPrimitive.Trigger>>({
  displayName: 'Popover.Trigger',
  preventOverride: true,
  renderDefault: ({ children, asChild = true, ...props }) => {
    return (
      <PopoverPrimitive.Trigger data-spellbook-name="popover-trigger" asChild={asChild} {...props}>
        {typeof children === 'string' && children.length > 0 ? <button type="button">{children}</button> : children}
      </PopoverPrimitive.Trigger>
    );
  },
});

export const PopoverTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & {
    'data-testid'?: string;
  }
>({
  displayName: 'Popover.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({ className, children, side = 'top', align = 'center', 'data-testid': dataTestId, ...props }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: PopoverTooltipContentSlot },
      trigger: { Component: PopoverTriggerSlot, props: { 'data-testid': dataTestId } },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} asToggleTip={false} {...props}>
        <Tooltip.Trigger>{slots.trigger}</Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const PopoverTooltipContentSlot = Slot<TextProps>({
  displayName: 'Popover.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => <Text data-spellbook-name="popover-tooltip-content" variant="caption-1" element="span" {...props} />,
});

export const PopoverContentSlot = Slot<React.ComponentProps<typeof PopoverPrimitive.Content>>({
  displayName: 'Popover.Content',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    const dir = useDirection();

    if (!children) {
      return null;
    }

    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-spellbook-name="popover-content"
          className={cn(
            ...popperContentSideOffsetClassNames,
            popoverContentClassName,
            ...popperContentMotionClassNames,
            'relative',
            'z-portal',
            'min-w-24',
            'max-w-(--radix-popper-available-width)',
            'max-h-(--radix-popper-available-height)',
            'origin-(--radix-popover-content-transform-origin)',
            'overflow-x-hidden',
            'overflow-y-auto',
            'group',

            className,
          )}
          dir={dir}
          {...props}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  },
});
